import { useMemo, useState } from "react";
import {
  useGetOrdersQuery,
  useGetFactoriesQuery,
  useGetWarehousesQuery,
  useDeleteOrderMutation,
} from "../../../../redux/api";
import type { Order } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { OrderFormModal } from "./order";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const STATUS_TONE: Record<string, string> = {
  draft: "badge--info",
  placed: "badge--warning",
  received: "badge--success",
  cancelled: "badge--danger",
};

const statusTone = (status?: string | null) =>
  STATUS_TONE[(status ?? "").toLowerCase()] ?? "badge--info";

const formatDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString() : "—";

// Inventory → Purchase Orders (list page).
const Orders = () => {
  const {
    data: orders = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetOrdersQuery();

  const { data: factories = [] } = useGetFactoriesQuery();
  const { data: warehouses = [] } = useGetWarehousesQuery();

  const [deleteOrder] = useDeleteOrderMutation();

  const [search, setSearch] = useState("");

  const factoryName = (id?: string | null) =>
    id ? factories.find((f) => f.id === id)?.name ?? id : "—";

  const warehouseName = (id?: string | null) =>
    id ? warehouses.find((w) => w.id === id)?.name ?? id : "—";

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = orders.find((o) => o.id === viewingId) ?? null;


  const handleDelete = async (order: Order) => {
    if (!window.confirm(`Delete order "${order.orderNumber}"?`)) return;
    try {
      await deleteOrder(order.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete order.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o: Order) =>
      [o.orderNumber, o.status, factoryName(o.factoryId)].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, search, factories]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load orders"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Orders"
          onBack={() => setViewingId(null)}
          title={viewing.orderNumber ?? viewing.id}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Order date", value: formatDate(viewing.orderDate) },
              { label: "Factory", value: factoryName(viewing.factoryId) },
              { label: "Warehouse", value: warehouseName(viewing.warehouseId) },
              { label: "Tax %", value: viewing.taxPercentage ?? 0 },
              { label: "Notes", value: viewing.notes, full: true },
            ]}
          />

          <h3 className="panel__section-title">Line items</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: "40%" }}>Product</th>
                  <th>Qty</th>
                  <th>Unit price</th>
                  <th>Line total</th>
                </tr>
              </thead>
              <tbody>
                {viewing.items.length === 0 ? (
                  <tr>
                    <td className="data-table__empty" colSpan={4}>
                      No items.
                    </td>
                  </tr>
                ) : (
                  viewing.items.map((row, index) => (
                    <tr key={index}>
                      <td>{row.productName || row.productId}</td>
                      <td>{row.quantity}</td>
                      <td>{row.unitPrice.toLocaleString()}</td>
                      <td>{row.lineTotal.toLocaleString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="totals">
            <div>
              <span>Subtotal</span>
              <strong>{viewing.subtotal.toLocaleString()}</strong>
            </div>
            <div>
              <span>Tax ({viewing.taxPercentage ?? 0}%)</span>
              <strong>{viewing.taxAmount.toLocaleString()}</strong>
            </div>
            <div className="totals__grand">
              <span>Total</span>
              <strong>{viewing.total.toLocaleString()}</strong>
            </div>
          </div>
        </EntityOverview>
        <OrderFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Purchase Orders</h2>
            <p className="panel__sub">
              Showing {visible.length} of {orders.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search orders…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New order
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Factory</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading orders…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {orders.length === 0
                      ? "No orders yet."
                      : "No orders match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((order: Order) => (
                  <tr key={order.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(order.id)}
                      >
                        {order.orderNumber}
                      </button>
                    </td>
                    <td>{factoryName(order.factoryId)}</td>
                    <td className="cell-date">{formatDate(order.orderDate)}</td>
                    <td className="cell-date">
                      {order.total?.toLocaleString?.() ?? order.total}
                    </td>
                    <td>
                      <span className={`badge ${statusTone(order.status)}`}>
                        <span className="badge__dot" />
                        {order.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(order.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(order.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(order),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <OrderFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Orders;
